/**
 * CountUp — animates a number from 0 to `value` when the element enters
 * the viewport. Runs once per mount so revisits feel snappy, not stale.
 *
 * Props:
 *   value       — target integer
 *   duration    — ms to animate (default 1200)
 *   format      — optional formatter (default: K/M abbreviation)
 *   className   — for styling
 *
 * Zero dependencies beyond React and IntersectionObserver.
 */
import { useEffect, useRef, useState } from "react";

function _defaultFormat(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000)     return (n /     1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(Math.floor(n));
}

export default function CountUp({
  value = 0,
  duration = 1400,
  format = _defaultFormat,
  className = "",
  ...rest
}) {
  const [display, setDisplay] = useState(0);
  const ref  = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    if (!ref.current || done.current) return;
    const el = ref.current;

    const run = () => {
      if (done.current) return;
      done.current = true;
      const start = performance.now();
      const target = Number(value) || 0;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        // easeOutQuart for a satisfying overshoot-free landing
        const eased = 1 - Math.pow(1 - t, 4);
        setDisplay(target * eased);
        if (t < 1) requestAnimationFrame(tick);
        else setDisplay(target);
      };
      requestAnimationFrame(tick);
    };

    // If IntersectionObserver isn't available, just run immediately.
    if (typeof IntersectionObserver === "undefined") { run(); return; }
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) run(); }); },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className={className} {...rest}>
      {format(display)}
    </span>
  );
}
